import { Center, Text, VStack } from '@chakra-ui/react';
import CustomButton from '@/components/atoms/CustomButton';
import colors from '@/theme/colors';

type Props = {
  onRetry: () => void;
  onSignOut: () => void;
};

/**
 * Shown when the session is valid but the account data could not be loaded.
 *
 * Neither the app nor /sign-in, deliberately. The user is signed in — the session
 * check proved it — so signing them out over a data outage would punish them for
 * something they did not do. But we cannot render the app either: the only
 * fallback data may belong to a different user, and showing it would leak. So the
 * honest state is a retry, with sign-out available as their choice.
 *
 * Deliberately self-contained: this renders from the auth provider, *above*
 * RouterProvider, so it must not reach for a page template. Those render Header,
 * which calls useRouterState and useNavigate and would throw with no router
 * mounted. Chakra primitives only, no router hooks.
 */
const AccountUnavailablePage = ({ onRetry, onSignOut }: Props) => (
  <Center width={'100vw'} height={'100vh'} backgroundColor={'background.lightBlue'} padding={'24px'}>
    <VStack gap={'30px'} maxWidth={'420px'}>
      <Text variant={'loraTitle'} textAlign={'center'} fontSize={'26px'}>
        We couldn&apos;t load your account
      </Text>
      <Text variant={'urbanistSemiBold'} textAlign={'center'}>
        Your session is fine — we just couldn&apos;t reach some of your data. Try again in a moment.
      </Text>
      <VStack gap={'12px'}>
        <CustomButton
          backgroundColor={colors.primary[500]}
          hoverBackgroundColor={colors.primary[500]}
          width={'200px'}
          height={'50px'}
          labelColor="white"
          onClick={onRetry}
          label="Try again"
        />
        <CustomButton isTransparent width={'200px'} onClick={onSignOut} label="Sign out" />
      </VStack>
    </VStack>
  </Center>
);

export default AccountUnavailablePage;
